// =============================================================================
// TenantManager.js - Gestão de Instalações com Configuração MELCloud
// =============================================================================
// Smart Home v6.0 - Componente React para gestão multi-tenant
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './TenantManager.css';

const API_BASE = '/api/tenants';

// Ícones inline (evita dependências externas)
const Icons = {
  Plus: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Settings: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  Trash: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  X: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Building: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
    </svg>
  ),
  Snowflake: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="M20 6l-8 6-8-6" />
      <path d="M4 18l8-6 8 6" />
    </svg>
  ),
  Sun: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  ),
  Zap: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  Loader: () => (
    <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" opacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  )
};

// =============================================================================
// Componente Principal
// =============================================================================

const TenantManager = () => {
  const { t } = useTranslation();
  // Estado principal
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' ou 'edit'
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    address: '',
    enabled_modules: ['ac']
  });
  
  // MELCloud state
  const [melcloudForm, setMelcloudForm] = useState({
    email: '',
    password: ''
  });
  const [melcloudTesting, setMelcloudTesting] = useState(false);
  const [melcloudTestResult, setMelcloudTestResult] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [savingMelcloud, setSavingMelcloud] = useState(false);

  // Sungrow state
  const [sungrowForm, setSungrowForm] = useState({
    gateway: 'https://gateway.isolarcloud.eu',
    app_key: '',
    secret_key: '',
    email: '',
    password: '',
    ps_id: ''
  });
  const [savingSungrow, setSavingSungrow] = useState(false);
  const [sungrowResult, setSungrowResult] = useState(null);

  // Home Connect state
  const [homeconnectConnected, setHomeconnectConnected] = useState(false);
  const [homeconnectAppliances, setHomeconnectAppliances] = useState([]);
  const [loadingHomeconnectAppliances, setLoadingHomeconnectAppliances] = useState(false);
  const [homeconnectNotice, setHomeconnectNotice] = useState(null);

  // LG ThinQ state
  const [lgthinqForm, setLgthinqForm] = useState({ pat: '', country: 'PT' });
  const [savingLgthinq, setSavingLgthinq] = useState(false);
  const [lgthinqResult, setLgthinqResult] = useState(null);
  const [lgthinqHasPat, setLgthinqHasPat] = useState(false);

  // Foto da instalação
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(null);

  // Token de autenticação
  const getAuthHeader = () => {
    const token = localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // =============================================================================
  // Carregar lista de tenants
  // =============================================================================
  
  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) throw new Error(t('gestaoInstalacoes.erroCarregar'));
      
      const data = await response.json();
      setTenants(data.tenants || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Mostrar resultado do redirect de autorização Home Connect (?homeconnect=success|error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('homeconnect');
    if (result) {
      setHomeconnectNotice(result === 'success'
        ? t('gestaoInstalacoes.hcLigadaOk')
        : t('gestaoInstalacoes.hcLigadaErro'));
      params.delete('homeconnect');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    }
  }, []);

  // =============================================================================
  // CRUD Operations
  // =============================================================================

  const handleCreateTenant = async () => {
    try {
      const response = await fetch(`${API_BASE}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('gestaoInstalacoes.erroCriar'));
      }
      
      await loadTenants();
      closeModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateTenant = async () => {
    if (!selectedTenant) return;
    
    try {
      const response = await fetch(`${API_BASE}/${selectedTenant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          name: formData.name,
          address: formData.address,
          enabled_modules: formData.enabled_modules
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('gestaoInstalacoes.erroAtualizar'));
      }
      
      await loadTenants();
      closeModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm(t('gestaoInstalacoes.confirmarEliminar'))) return;
    
    try {
      const response = await fetch(`${API_BASE}/${tenantId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('gestaoInstalacoes.erroEliminar'));
      }
      
      await loadTenants();
    } catch (err) {
      setError(err.message);
    }
  };

  // =============================================================================
  // Foto da Instalação
  // =============================================================================

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedTenant) return;

    setUploadingPhoto(true);
    setPhotoError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE}/${selectedTenant.id}/photo`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('gestaoInstalacoes.erroFoto'));
      }

      const data = await response.json();
      setSelectedTenant(prev => ({ ...prev, photo_url: data.photo_url }));
      await loadTenants();
    } catch (err) {
      setPhotoError(err.message);
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // =============================================================================
  // MELCloud Operations
  // =============================================================================

  const loadTenantDetails = async (tenantId) => {
    try {
      const response = await fetch(`${API_BASE}/${tenantId}`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) throw new Error(t('gestaoInstalacoes.erroDetalhes'));
      
      const data = await response.json();
      
      // Preencher formulário MELCloud se existir
      if (data.config?.melcloud) {
        setMelcloudForm({
          email: data.config.melcloud.email || '',
          password: '' // Nunca pre-preencher password
        });
        setSelectedBuildingId(data.config.melcloud.building_id);
      }
      
      // Preencher formulário Sungrow se existir
      if (data.config?.sungrow) {
        setSungrowForm({
          gateway: data.config.sungrow.gateway || 'https://gateway.isolarcloud.eu',
          app_key: '', // Nunca pre-preencher chaves
          secret_key: '', // Nunca pre-preencher chaves
          email: data.config.sungrow.email || '',
          password: '', // Nunca pre-preencher password
          ps_id: data.config.sungrow.ps_id || ''
        });
      }

      // Estado da ligação Home Connect
      setHomeconnectConnected(!!data.config?.homeconnect?.connected);
      setHomeconnectAppliances([]);

      // Estado da ligação LG ThinQ
      setLgthinqHasPat(!!data.config?.lgthinq?.has_pat);
      setLgthinqForm({ pat: '', country: data.config?.lgthinq?.country || 'PT' });

      return data;
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      return null;
    }
  };

  const testMelcloudConnection = async () => {
    if (!selectedTenant) return;
    
    // Primeiro guardar as credenciais
    if (melcloudForm.email && melcloudForm.password) {
      await saveMelcloudCredentials();
    }
    
    setMelcloudTesting(true);
    setMelcloudTestResult(null);
    
    try {
      const response = await fetch(`${API_BASE}/${selectedTenant.id}/melcloud/test`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      
      const data = await response.json();
      setMelcloudTestResult(data);
      
      // Se sucesso, carregar buildings
      if (data.success) {
        await loadBuildings();
      }
    } catch (err) {
      setMelcloudTestResult({ success: false, error: err.message });
    } finally {
      setMelcloudTesting(false);
    }
  };

  const saveMelcloudCredentials = async () => {
    if (!selectedTenant || !melcloudForm.email || !melcloudForm.password) return;
    
    setSavingMelcloud(true);
    
    try {
      const response = await fetch(`${API_BASE}/${selectedTenant.id}/config/melcloud`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          email: melcloudForm.email,
          password: melcloudForm.password,
          building_id: selectedBuildingId
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('gestaoInstalacoes.erroCredenciais'));
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Limpar password do form após guardar com sucesso
        setMelcloudForm(prev => ({ ...prev, password: '' }));
        setMelcloudTestResult({ success: true, message: t('gestaoInstalacoes.credenciaisGuardadas') });
        
        // Recarregar lista para atualizar status
        await loadTenants();
      }
    } catch (err) {
      setMelcloudTestResult({ success: false, error: err.message });
    } finally {
      setSavingMelcloud(false);
    }
  };

  const saveSungrowConfig = async () => {
    if (!selectedTenant) return;
    
    // Validar campos obrigatórios
    if (!sungrowForm.app_key || !sungrowForm.secret_key || !sungrowForm.email || !sungrowForm.password || !sungrowForm.ps_id) {
      setSungrowResult({ success: false, error: t('gestaoInstalacoes.camposObrigatorios') });
      return;
    }
    
    setSavingSungrow(true);
    setSungrowResult(null);
    
    try {
      const response = await fetch(`${API_BASE}/${selectedTenant.id}/config/sungrow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          gateway: sungrowForm.gateway,
          app_key: sungrowForm.app_key,
          secret_key: sungrowForm.secret_key,
          email: sungrowForm.email,
          password: sungrowForm.password,
          ps_id: sungrowForm.ps_id
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('gestaoInstalacoes.erroConfiguracao'));
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Limpar campos sensíveis após guardar com sucesso
        setSungrowForm(prev => ({ 
          ...prev, 
          app_key: '', 
          secret_key: '', 
          password: '' 
        }));
        setSungrowResult({ success: true, message: t('gestaoInstalacoes.sungrowGuardada') });
        
        // Recarregar lista para atualizar status
        await loadTenants();
      }
    } catch (err) {
      setSungrowResult({ success: false, error: err.message });
    } finally {
      setSavingSungrow(false);
    }
  };

  // =============================================================================
  // Home Connect Operations
  // =============================================================================

  const connectHomeconnect = () => {
    if (!selectedTenant) return;
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({ tenant_id: selectedTenant.id, token });
    window.open(`/api/homeconnect/authorize?${params.toString()}`, '_blank');
  };

  // =============================================================================
  // LG ThinQ Operations
  // =============================================================================

  const saveLgthinqConfig = async () => {
    if (!selectedTenant || !lgthinqForm.pat) return;

    setSavingLgthinq(true);
    setLgthinqResult(null);

    try {
      const response = await fetch(`${API_BASE}/${selectedTenant.id}/config/lgthinq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ pat: lgthinqForm.pat, country: lgthinqForm.country })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('gestaoInstalacoes.erroConfiguracao'));
      }

      setLgthinqHasPat(true);
      setLgthinqForm(prev => ({ ...prev, pat: '' }));
      setLgthinqResult({ success: true, message: t('gestaoInstalacoes.lgGuardada') });
      await loadTenants();
    } catch (err) {
      setLgthinqResult({ success: false, error: err.message });
    } finally {
      setSavingLgthinq(false);
    }
  };

  const testLgthinqConnection = async () => {
    if (!selectedTenant) return;

    setSavingLgthinq(true);
    setLgthinqResult(null);

    try {
      const response = await fetch(`/api/lgthinq/devices?tenant_id=${selectedTenant.id}`, {
        headers: getAuthHeader()
      });

      if (!response.ok) throw new Error(t('gestaoInstalacoes.erroTestar'));

      const data = await response.json();
      if (data.connected && data.devices?.length > 0) {
        setLgthinqResult({ success: true, message: `Conectado! ${data.devices.length} aparelho(s) encontrado(s).` });
      } else if (data.connected) {
        setLgthinqResult({ success: true, message: t('gestaoInstalacoes.lgSemAparelhos') });
      } else {
        setLgthinqResult({ success: false, error: 'PAT não configurado ou inválido' });
      }
    } catch (err) {
      setLgthinqResult({ success: false, error: err.message });
    } finally {
      setSavingLgthinq(false);
    }
  };

  const loadHomeconnectAppliances = async () => {
    if (!selectedTenant) return;

    setLoadingHomeconnectAppliances(true);
    try {
      const response = await fetch(`/api/homeconnect/appliances?tenant_id=${selectedTenant.id}`, {
        headers: getAuthHeader()
      });

      if (!response.ok) throw new Error(t('gestaoInstalacoes.erroAparelhos'));

      const data = await response.json();
      setHomeconnectConnected(data.connected);
      setHomeconnectAppliances(data.appliances || []);
    } catch (err) {
      console.error('Erro ao carregar aparelhos Home Connect:', err);
    } finally {
      setLoadingHomeconnectAppliances(false);
    }
  };

  const loadBuildings = async () => {
    if (!selectedTenant) return;
    
    setLoadingBuildings(true);
    
    try {
      const response = await fetch(`${API_BASE}/${selectedTenant.id}/melcloud/buildings`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('gestaoInstalacoes.erroBuildings'));
      }
      
      const data = await response.json();
      setBuildings(data.buildings || []);
      setSelectedBuildingId(data.current_building_id);
    } catch (err) {
      console.error('Erro ao carregar buildings:', err);
    } finally {
      setLoadingBuildings(false);
    }
  };

  const selectBuilding = async (buildingId) => {
    if (!selectedTenant) return;
    
    try {
      const response = await fetch(`${API_BASE}/${selectedTenant.id}/melcloud/building/${buildingId}`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('gestaoInstalacoes.erroSelecionarBuilding'));
      }
      
      setSelectedBuildingId(buildingId);
      await loadTenants(); // Recarregar para atualizar building_id na lista
    } catch (err) {
      setError(err.message);
    }
  };

  // =============================================================================
  // Modal Handlers
  // =============================================================================

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({
      name: '',
      slug: '',
      address: '',
      enabled_modules: ['ac']
    });
    setMelcloudForm({ email: '', password: '' });
    setMelcloudTestResult(null);
    setBuildings([]);
    setSelectedBuildingId(null);
    setActiveTab('general');
    setSelectedTenant(null);
    setShowModal(true);
  };

  const openEditModal = async (tenant) => {
    setModalMode('edit');
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      slug: tenant.slug,
      address: tenant.address || '',
      enabled_modules: tenant.enabled_modules || ['ac']
    });
    setMelcloudForm({ email: '', password: '' });
    setMelcloudTestResult(null);
    setBuildings([]);
    setSelectedBuildingId(null);
    setActiveTab('general');
    setShowModal(true);
    
    // Carregar detalhes completos
    await loadTenantDetails(tenant.id);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTenant(null);
    setError(null);
    // Limpar estados MELCloud
    setMelcloudForm({ email: '', password: '' });
    setMelcloudTestResult(null);
    setBuildings([]);
    setSelectedBuildingId(null);
    // Limpar estados Sungrow
    setSungrowForm({
      gateway: 'https://gateway.isolarcloud.eu',
      app_key: '',
      secret_key: '',
      email: '',
      password: '',
      ps_id: ''
    });
    setSungrowResult(null);
  };

  const handleSubmit = () => {
    if (modalMode === 'create') {
      handleCreateTenant();
    } else {
      handleUpdateTenant();
    }
  };

  // =============================================================================
  // Helpers
  // =============================================================================

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: modalMode === 'create' ? generateSlug(name) : prev.slug
    }));
  };

  const toggleModule = (module) => {
    setFormData(prev => ({
      ...prev,
      enabled_modules: prev.enabled_modules.includes(module)
        ? prev.enabled_modules.filter(m => m !== module)
        : [...prev.enabled_modules, module]
    }));
  };

  const AVAILABLE_MODULES = [
    { id: 'ac', icon: <Icons.Snowflake /> },
    { id: 'solar', icon: <Icons.Sun /> },
    { id: 'cameras', icon: <Icons.Building /> },
    { id: 'blinds', icon: <Icons.Building /> },
    { id: 'gates', icon: <Icons.Building /> },
    { id: 'heatpump', icon: <Icons.Zap /> },
    { id: 'vmc', icon: <Icons.Zap /> },
    { id: 'leds', icon: <Icons.Zap /> },
    { id: 'homeconnect', icon: <Icons.Zap /> },
    { id: 'lgthinq', icon: <Icons.Zap /> },
    { id: 'lgtv', icon: <Icons.Zap /> },
    { id: 'roborock', icon: <Icons.Zap /> }
  ];

  // =============================================================================
  // Render
  // =============================================================================

  if (loading && tenants.length === 0) {
    return (
      <div className="tenant-manager">
        <div className="loading-state">
          <Icons.Loader /> Carregando instalações...
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-manager">
      {/* Header */}
      <div className="tm-header">
        <h2>{t('gestaoInstalacoes.titulo')}</h2>
        <button className="btn btn-primary" onClick={openCreateModal}>
          <Icons.Plus /> {t('gestaoInstalacoes.nova')}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Home Connect authorization notice */}
      {homeconnectNotice && (
        <div className="error-message">
          {homeconnectNotice}
          <button onClick={() => setHomeconnectNotice(null)}>×</button>
        </div>
      )}

      {/* Tenant List */}
      <div className="tenant-list">
        {tenants.map(tenant => (
          <div key={tenant.id} className={`tenant-card ${!tenant.is_active ? 'inactive' : ''}`}>
            {tenant.photo_url ? (
              <img src={tenant.photo_url} alt={tenant.name} className="tenant-card-photo" />
            ) : (
              <div className="tenant-card-photo tenant-card-photo-placeholder">
                <Icons.Building />
              </div>
            )}
            <div className="tenant-info">
              <h3>{tenant.name}</h3>
              <span className="tenant-slug">/{tenant.slug}</span>
              {tenant.address && <p className="tenant-address">{tenant.address}</p>}
              
              <div className="tenant-badges">
                {tenant.has_melcloud && (
                  <span className="badge badge-melcloud" title={tenant.building_id ? `Building: ${tenant.building_id}` : t('gestaoInstalacoes.semBuilding')}>
                    <Icons.Snowflake /> MELCloud
                    {tenant.building_id && <span className="building-id">#{tenant.building_id}</span>}
                  </span>
                )}
                {tenant.has_sungrow && (
                  <span className="badge badge-sungrow">
                    <Icons.Sun /> Sungrow
                  </span>
                )}
                <span className="badge badge-users">
                  {t('gestaoInstalacoes.utilizadores', { count: tenant.user_count || 0 })}
                </span>
              </div>
            </div>
            
            <div className="tenant-actions">
              <button 
                className="btn btn-icon" 
                onClick={() => openEditModal(tenant)}
                title={t('gestaoInstalacoes.configurar')}
              >
                <Icons.Settings />
              </button>
              {tenant.slug !== 'default' && (
                <button 
                  className="btn btn-icon btn-danger" 
                  onClick={() => handleDeleteTenant(tenant.id)}
                  title={t('gestaoInstalacoes.eliminar')}
                >
                  <Icons.Trash />
                </button>
              )}
            </div>
          </div>
        ))}
        
        {tenants.length === 0 && (
          <div className="empty-state">
            <p>{t('gestaoInstalacoes.nenhuma')}</p>
            <button className="btn btn-primary" onClick={openCreateModal}>
              <Icons.Plus /> {t('gestaoInstalacoes.criarPrimeira')}
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'create' ? t('gestaoInstalacoes.nova') : t('gestaoInstalacoes.editarTitulo', { nome: selectedTenant?.name })}</h3>
              <button className="btn btn-icon" onClick={closeModal}>
                <Icons.X />
              </button>
            </div>

            {/* Tabs */}
            <div className="modal-tabs">
              <button 
                className={`tab ${activeTab === 'general' ? 'active' : ''}`}
                onClick={() => setActiveTab('general')}
              >
                {t('gestaoInstalacoes.tabGeral')}
              </button>
              {modalMode === 'edit' && (
                <>
                  <button 
                    className={`tab ${activeTab === 'melcloud' ? 'active' : ''}`}
                    onClick={() => setActiveTab('melcloud')}
                  >
                    <Icons.Snowflake /> MELCloud
                  </button>
                  <button
                    className={`tab ${activeTab === 'sungrow' ? 'active' : ''}`}
                    onClick={() => setActiveTab('sungrow')}
                  >
                    <Icons.Sun /> Sungrow
                  </button>
                  <button
                    className={`tab ${activeTab === 'homeconnect' ? 'active' : ''}`}
                    onClick={() => setActiveTab('homeconnect')}
                  >
                    <Icons.Zap /> Home Connect
                  </button>
                  <button
                    className={`tab ${activeTab === 'lgthinq' ? 'active' : ''}`}
                    onClick={() => setActiveTab('lgthinq')}
                  >
                    <Icons.Zap /> LG ThinQ
                  </button>
                </>
              )}
            </div>

            <div className="modal-body">
              {/* Tab: General */}
              {activeTab === 'general' && (
                <div className="tab-content">
                  <div className="form-group">
                    <label>{t('gestaoInstalacoes.nomeInstalacao')}</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={handleNameChange}
                      placeholder={t('gestaoInstalacoes.placeholderNome')}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('gestaoInstalacoes.slug')}</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={e => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder={t('gestaoInstalacoes.placeholderSlug')}
                      disabled={modalMode === 'edit'}
                    />
                    <small>{t('gestaoInstalacoes.ajudaSlug')}</small>
                  </div>

                  <div className="form-group">
                    <label>{t('gestaoInstalacoes.morada')}</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={e => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder={t('gestaoInstalacoes.placeholderMorada')}
                    />
                  </div>

                  {modalMode === 'edit' && (
                    <div className="form-group">
                      <label>{t('gestaoInstalacoes.foto')}</label>
                      <div className="tenant-photo-upload">
                        {selectedTenant?.photo_url ? (
                          <img
                            src={selectedTenant.photo_url}
                            alt={selectedTenant.name}
                            className="tenant-photo-preview"
                          />
                        ) : (
                          <div className="tenant-photo-placeholder">
                            <Icons.Building />
                          </div>
                        )}
                        <label className="btn btn-secondary btn-small tenant-photo-input-label">
                          {uploadingPhoto ? <><Icons.Loader /> {t('gestaoInstalacoes.aCarregarFoto')}</> : t('gestaoInstalacoes.escolherFoto')}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            disabled={uploadingPhoto}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                      {photoError && <small className="form-error">{photoError}</small>}
                    </div>
                  )}

                  <div className="form-group">
                    <label>{t('gestaoInstalacoes.modulosAtivos')}</label>
                    <div className="modules-grid">
                      {AVAILABLE_MODULES.map(module => (
                        <label key={module.id} className="module-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.enabled_modules.includes(module.id)}
                            onChange={() => toggleModule(module.id)}
                          />
                          <span className="module-label">
                            {module.icon} {t('gestaoInstalacoes.modulos.' + module.id)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Tab: MELCloud */}
              {activeTab === 'melcloud' && modalMode === 'edit' && (
                <div className="tab-content">
                  <div className="config-section">
                    <h4><Icons.Snowflake /> {t('gestaoInstalacoes.credenciaisMelcloud')}</h4>
                    
                    <div className="form-group">
                      <label>{t('gestaoInstalacoes.email')}</label>
                      <input
                        type="email"
                        value={melcloudForm.email}
                        onChange={e => setMelcloudForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="email@melcloud.com"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('gestaoInstalacoes.password')}</label>
                      <input
                        type="password"
                        value={melcloudForm.password}
                        onChange={e => setMelcloudForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="••••••••"
                      />
                      <small>{t('gestaoInstalacoes.ajudaPassword')}</small>
                    </div>

                    <div className="button-group">
                      <button 
                        className="btn btn-secondary"
                        onClick={testMelcloudConnection}
                        disabled={melcloudTesting || (!melcloudForm.email)}
                      >
                        {melcloudTesting ? <><Icons.Loader /> {t('gestaoInstalacoes.aTestar')}</> : t('gestaoInstalacoes.testarLigacao')}
                      </button>
                      
                      <button 
                        className="btn btn-primary"
                        onClick={saveMelcloudCredentials}
                        disabled={savingMelcloud || !melcloudForm.email || !melcloudForm.password}
                      >
                        {savingMelcloud ? <><Icons.Loader /> {t('gestaoInstalacoes.aGuardar')}</> : t('gestaoInstalacoes.guardarCredenciais')}
                      </button>
                    </div>

                    {/* Test Result */}
                    {melcloudTestResult && (
                      <div className={`test-result ${melcloudTestResult.success ? 'success' : 'error'}`}>
                        {melcloudTestResult.success ? (
                          <>
                            <Icons.Check /> 
                            {melcloudTestResult.message || `Conectado! ${melcloudTestResult.buildings_count} building(s), ${melcloudTestResult.devices_count} dispositivo(s)`}
                          </>
                        ) : (
                          <>❌ {melcloudTestResult.error}</>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Buildings Section */}
                  <div className="config-section">
                    <h4>
                      <Icons.Building /> {t('gestaoInstalacoes.selecionarBuilding')}
                      <button 
                        className="btn btn-small btn-secondary"
                        onClick={loadBuildings}
                        disabled={loadingBuildings}
                      >
                        {loadingBuildings ? <Icons.Loader /> : t('comum.atualizar')}
                      </button>
                    </h4>
                    
                    <p className="section-help">
                      {t('gestaoInstalacoes.ajudaBuilding')}
                    </p>

                    {buildings.length > 0 ? (
                      <div className="buildings-list">
                        {buildings.map(building => (
                          <div 
                            key={building.id}
                            className={`building-card ${selectedBuildingId === building.id ? 'selected' : ''}`}
                            onClick={() => selectBuilding(building.id)}
                          >
                            <div className="building-info">
                              <strong>{building.name}</strong>
                              <span className="building-id">ID: {building.id}</span>
                              {building.address && <span className="building-address">{building.address}</span>}
                            </div>
                            <div className="building-devices">
                              <span className="device-count">
                                <Icons.Snowflake /> {building.device_count} AC
                              </span>
                              {selectedBuildingId === building.id && (
                                <Icons.Check className="check-icon" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-buildings">
                        <p>{t('gestaoInstalacoes.semBuildings')}</p>
                      </div>
                    )}

                    {selectedBuildingId && (
                      <div className="selected-building-info">
                        ✅ {t('gestaoInstalacoes.buildingSelecionado')} <strong>{selectedBuildingId}</strong>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tab: Sungrow */}
              {activeTab === 'sungrow' && modalMode === 'edit' && (
                <div className="tab-content">
                  <div className="config-section">
                    <h4><Icons.Sun /> {t('gestaoInstalacoes.configSungrow')}</h4>
                    <p className="section-help">
                      Configuração para integração com sistema solar Sungrow iSolarCloud.
                    </p>
                    
                    {/* Credenciais API */}
                    <div className="form-group">
                      <label>{t('gestaoInstalacoes.gatewayApi')}</label>
                      <select
                        value={sungrowForm.gateway}
                        onChange={(e) => setSungrowForm({...sungrowForm, gateway: e.target.value})}
                      >
                        <option value="https://gateway.isolarcloud.eu">{t('gestaoInstalacoes.gatewayEuropa')}</option>
                        <option value="https://gateway.isolarcloud.com">{t('gestaoInstalacoes.gatewayGlobal')}</option>
                        <option value="https://augateway.isolarcloud.com">{t('gestaoInstalacoes.gatewayAustralia')}</option>
                      </select>
                      <small className="form-hint">{t('gestaoInstalacoes.ajudaGateway')}</small>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('gestaoInstalacoes.appKey')}</label>
                        <input
                          type="text"
                          value={sungrowForm.app_key}
                          onChange={(e) => setSungrowForm({...sungrowForm, app_key: e.target.value})}
                          placeholder={t('gestaoInstalacoes.placeholderAppKey')}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('gestaoInstalacoes.secretKey')}</label>
                        <input
                          type="password"
                          value={sungrowForm.secret_key}
                          onChange={(e) => setSungrowForm({...sungrowForm, secret_key: e.target.value})}
                          placeholder={t('gestaoInstalacoes.placeholderSecretKey')}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('gestaoInstalacoes.emailSolar')}</label>
                        <input
                          type="email"
                          value={sungrowForm.email}
                          onChange={(e) => setSungrowForm({...sungrowForm, email: e.target.value})}
                          placeholder={t('gestaoInstalacoes.placeholderEmailSolar')}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('gestaoInstalacoes.passwordObrigatoria')}</label>
                        <input
                          type="password"
                          value={sungrowForm.password}
                          onChange={(e) => setSungrowForm({...sungrowForm, password: e.target.value})}
                          placeholder={t('gestaoInstalacoes.placeholderPasswordConta')}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>{t('gestaoInstalacoes.psId')}</label>
                      <input
                        type="text"
                        value={sungrowForm.ps_id}
                        onChange={(e) => setSungrowForm({...sungrowForm, ps_id: e.target.value})}
                        placeholder={t('gestaoInstalacoes.placeholderPsId')}
                      />
                      <small className="form-hint">
                        {t('gestaoInstalacoes.ajudaPsId')}
                      </small>
                    </div>

                    {/* Resultado */}
                    {sungrowResult && (
                      <div className={`test-result ${sungrowResult.success ? 'success' : 'error'}`}>
                        {sungrowResult.success ? (
                          <>
                            <Icons.Check />
                            <span>{sungrowResult.message}</span>
                          </>
                        ) : (
                          <>
                            <Icons.X />
                            <span>{sungrowResult.error}</span>
                          </>
                        )}
                      </div>
                    )}

                    {/* Botão Guardar */}
                    <div className="button-group">
                      <button 
                        className="btn btn-primary"
                        onClick={saveSungrowConfig}
                        disabled={savingSungrow || !sungrowForm.app_key || !sungrowForm.secret_key || !sungrowForm.email || !sungrowForm.password || !sungrowForm.ps_id}
                      >
                        {savingSungrow ? (
                          <>
                            <Icons.Loader />
                            {t('gestaoInstalacoes.aGuardar')}
                          </>
                        ) : (
                          <>
                            <Icons.Check />
                            {t('gestaoInstalacoes.guardarSungrow')}
                          </>
                        )}
                      </button>
                    </div>

                    {/* Info adicional */}
                    <div className="info-box">
                      <h5>{t('gestaoInstalacoes.comoObterCredenciais')}</h5>
                      <ol>
                        <li>{t('gestaoInstalacoes.passoPortal')} <a href="https://developer.isolarcloud.com" target="_blank" rel="noopener noreferrer">{t('gestaoInstalacoes.portalDevelopers')}</a></li>
                        <li>{t('gestaoInstalacoes.passoRegistar')}</li>
                        <li>{t('gestaoInstalacoes.passoMesmasCredenciais')}</li>
                        <li>{t('gestaoInstalacoes.passoPsId')}</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab: Home Connect */}
              {activeTab === 'homeconnect' && modalMode === 'edit' && (
                <div className="tab-content">
                  <div className="config-section">
                    <h4><Icons.Zap /> {t('gestaoInstalacoes.homeConnectTitulo')}</h4>
                    <p className="section-help">
                      Liga a conta Home Connect desta instalação para trazer os
                      eletrodomésticos ligados (ex: máquina de lavar loiça, exaustor).
                    </p>

                    <div className={`test-result ${homeconnectConnected ? 'success' : ''}`}>
                      {homeconnectConnected ? (
                        <>
                          <Icons.Check />
                          <span>{t('gestaoInstalacoes.contaLigada')}</span>
                        </>
                      ) : (
                        <span>{t('gestaoInstalacoes.contaNaoLigada')}</span>
                      )}
                    </div>

                    <div className="button-group">
                      <button className="btn btn-primary" onClick={connectHomeconnect}>
                        <Icons.Zap /> {homeconnectConnected ? t('gestaoInstalacoes.ligarNovamente') : t('gestaoInstalacoes.ligarConta')}
                      </button>
                      {homeconnectConnected && (
                        <button
                          className="btn btn-secondary"
                          onClick={loadHomeconnectAppliances}
                          disabled={loadingHomeconnectAppliances}
                        >
                          {loadingHomeconnectAppliances ? (
                            <><Icons.Loader /> {t('gestaoInstalacoes.aObterAparelhos')}</>
                          ) : (
                            t('gestaoInstalacoes.verAparelhos')
                          )}
                        </button>
                      )}
                    </div>

                    {homeconnectAppliances.length > 0 && (
                      <ul className="appliance-list">
                        {homeconnectAppliances.map(app => (
                          <li key={app.ha_id}>
                            <strong>{app.name}</strong> ({app.type}) — {app.connected ? 'online' : 'offline'}
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="info-box">
                      <h5>{t('gestaoInstalacoes.comoFunciona')}</h5>
                      <ol>
                        <li>{t('gestaoInstalacoes.passoHc1')}</li>
                        <li>{t('gestaoInstalacoes.passoHc2')}</li>
                        <li>{t('gestaoInstalacoes.passoHc3')}</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab: LG ThinQ */}
              {activeTab === 'lgthinq' && modalMode === 'edit' && (
                <div className="tab-content">
                  <div className="config-section">
                    <h4><Icons.Zap /> {t('gestaoInstalacoes.lgThinqTitulo')}</h4>
                    <p className="section-help">
                      Liga a conta LG ThinQ desta instalação usando um Personal
                      Access Token (PAT), gerado em connect-pat.lgthinq.com.
                    </p>

                    {lgthinqHasPat && (
                      <div className="test-result success">
                        <Icons.Check />
                        <span>{t('gestaoInstalacoes.patConfigurado')}</span>
                      </div>
                    )}

                    <div className="form-group">
                      <label>{t('gestaoInstalacoes.labelPat')}</label>
                      <input
                        type="password"
                        value={lgthinqForm.pat}
                        onChange={e => setLgthinqForm(prev => ({ ...prev, pat: e.target.value }))}
                        placeholder={lgthinqHasPat ? t('gestaoInstalacoes.placeholderPatExistente') : 'thinqpat_...'}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('gestaoInstalacoes.countryCode')}</label>
                      <input
                        type="text"
                        value={lgthinqForm.country}
                        onChange={e => setLgthinqForm(prev => ({ ...prev, country: e.target.value.toUpperCase() }))}
                        placeholder="PT"
                        maxLength={2}
                      />
                    </div>

                    <div className="button-group">
                      <button
                        className="btn btn-secondary"
                        onClick={testLgthinqConnection}
                        disabled={savingLgthinq || !lgthinqHasPat}
                      >
                        {savingLgthinq ? <><Icons.Loader /> {t('gestaoInstalacoes.aTestar')}</> : t('gestaoInstalacoes.testarLigacao')}
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={saveLgthinqConfig}
                        disabled={savingLgthinq || !lgthinqForm.pat}
                      >
                        {savingLgthinq ? <><Icons.Loader /> {t('gestaoInstalacoes.aGuardar')}</> : t('gestaoInstalacoes.guardarPat')}
                      </button>
                    </div>

                    {lgthinqResult && (
                      <div className={`test-result ${lgthinqResult.success ? 'success' : 'error'}`}>
                        {lgthinqResult.success ? (
                          <><Icons.Check /> {lgthinqResult.message}</>
                        ) : (
                          <>❌ {lgthinqResult.error}</>
                        )}
                      </div>
                    )}

                    <div className="info-box">
                      <h5>{t('gestaoInstalacoes.comoObterPat')}</h5>
                      <ol>
                        <li>{t('gestaoInstalacoes.passoAcedaA')} <a href="https://connect-pat.lgthinq.com" target="_blank" rel="noopener noreferrer">connect-pat.lgthinq.com</a> {t('gestaoInstalacoes.passoPat1')}</li>
                        <li>{t('gestaoInstalacoes.passoPat2')}</li>
                        <li>{t('gestaoInstalacoes.passoPat3')}</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancelar
              </button>
              {activeTab === 'general' && (
                <button className="btn btn-primary" onClick={handleSubmit}>
                  {modalMode === 'create' ? t('gestaoInstalacoes.criarInstalacao') : t('gestaoInstalacoes.guardarAlteracoes')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManager;
